import { useMemo, useState } from 'react';
import { useTabState } from '../lib/tabParam';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { Tabs, DataTable, StatCard, CreateEntityModal, Modal, useToast, EntityNotesPanel, LastUpdated } from '../organisms';
import { EntityHeader } from '../shell';
import { formatDate } from '../lib/orgTimezone';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * InvoiceDetail — /v2/invoices/:id
 *
 * Bespoke detail with the invoice status-machine workflow buttons
 * (pending → prepared → approved → sent → paid) and Record Payment
 * modal that hits /api/workflow/mark-invoice-paid/:id.
 *
 * Phase 11 Block 10 PR 3 (2026-05-12).
 */

type InvoiceStatus =
  | 'pending' | 'prepared' | 'approved' | 'sent'
  | 'partially_paid' | 'paid' | 'void' | 'cancelled';

interface Invoice {
  id: string;
  client_id: string;
  property_id: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  invoice_number: string;
  type: string | null;
  status: InvoiceStatus;
  status_date: string | null;
  year: number;
  amount: number | null;
  balance: number | null;
  last_paid_date?: string | null;
  due_date?: string | null;
  notes?: string | null;
}

interface InvoiceItem {
  id: string;
  invoice_id: string;
  description: string | null;
  amount: number | null;
  parcel_id: string | null;
}

interface InvoicePayment {
  id: string;
  invoice_id: string;
  amount: number | null;
  method: string | null;
  status: string | null;
  payment_date: string | null;
  check_number: string | null;
  reference_number: string | null;
  notes: string | null;
}

type InstallmentStatus = 'pending' | 'paid' | 'overdue' | 'cancelled';

interface Installment {
  id: string;
  invoice_id: string;
  sequence: number;
  due_date: string;
  amount: number | null;
  status: InstallmentStatus;
  paid_date: string | null;
  reminder_sent_at?: string | null;
}

interface Client { id: string; name: string; }

// Provider-neutral cash ledger for the invoice (GET /api/billing/ledger/:id).
// We only read the external_payment_refs statuses to surface the ACH settlement
// lifecycle (05.4): 'processing' = debit in flight, held pending settlement;
// 'returned' = ACH bounced / NSF, invoice reverted to unpaid. Money fields are
// stripped server-side for money-blind roles — we never render ref amounts here.
interface ExternalPaymentRef { id: string; provider_ref: string; status: string | null; }
interface LedgerResponse { refs: ExternalPaymentRef[] }

const statusVariant: Record<InvoiceStatus, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  pending: 'neutral',
  prepared: 'info',
  approved: 'info',
  sent: 'warning',
  partially_paid: 'warning',
  paid: 'success',
  void: 'danger',
  cancelled: 'danger',
};

// Next-step button per current status. Maps to PUT /api/invoices/:id with
// the destination status (the generic CRUD validates the transition against
// INVOICE_STATUS_FLOW in src/config/enums.js).
const nextStatusStep: Partial<Record<InvoiceStatus, { status: InvoiceStatus; label: string }>> = {
  pending: { status: 'prepared', label: 'Prepare' },
  prepared: { status: 'approved', label: 'Approve' },
  approved: { status: 'sent', label: 'Send to Client' },
};

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useTabState(['items', 'payments', 'installments', 'notes'] as const, 'items');
  const [editOpen, setEditOpen] = useState(false);
  const [confirmStatusStep, setConfirmStatusStep] = useState<InvoiceStatus | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [editInstallment, setEditInstallment] = useState<Installment | null>(null);

  const invoiceQuery = useQuery<Invoice>({
    queryKey: ['invoices', id],
    queryFn: ({ signal }) => api.get<Invoice>(`/api/invoices/${id}`, signal),
    enabled: !!id,
  });

  const clientQuery = useQuery<Client>({
    queryKey: ['client', invoiceQuery.data?.client_id],
    queryFn: ({ signal }) => api.get<Client>(`/api/clients/${invoiceQuery.data!.client_id}`, signal),
    enabled: !!invoiceQuery.data?.client_id,
  });

  const itemsQuery = useQuery<InvoiceItem[]>({
    queryKey: ['invoice-items', id],
    queryFn: ({ signal }) => api.get<InvoiceItem[]>(`/api/invoice-items?invoice_id=${id}`, signal),
    enabled: !!id,
  });

  const paymentsQuery = useQuery<InvoicePayment[]>({
    queryKey: ['invoice-payments', id],
    queryFn: ({ signal }) => api.get<InvoicePayment[]>(`/api/invoice-payments?invoice_id=${id}`, signal),
    enabled: !!id,
  });

  // Installment schedule (Domain 05 · invoice_installments generic-CRUD list,
  // ordered by sequence). Money-blind roles get amount stripped at res.json.
  const installmentsQuery = useQuery<Installment[]>({
    queryKey: ['invoice-installments', id],
    queryFn: ({ signal }) => api.get<Installment[]>(`/api/invoice-installments?invoice_id=${id}&sort=sequence&dir=asc`, signal),
    enabled: !!id,
  });

  // Cash ledger — read the Stripe provider-ref statuses to surface the ACH
  // settlement state. Only meaningful once an invoice can be collected on.
  const ledgerQuery = useQuery<LedgerResponse>({
    queryKey: ['invoice-ledger', id],
    queryFn: ({ signal }) => api.get<LedgerResponse>(`/api/billing/ledger/${id}`, signal),
    enabled: !!id,
  });

  // Notes count for the tab badge — shares EntityNotesPanel's query key so the
  // fetch is deduped and any add/edit/delete invalidation refreshes both. These
  // are the invoice's firm notes; the owning client also sees them in the portal.
  const notesQuery = useQuery<Array<Record<string, unknown>>>({
    queryKey: ['entity-notes', 'invoices', id],
    queryFn: ({ signal }) => api.get<Array<Record<string, unknown>>>(`/api/notes-enriched/invoices/${id}`, signal),
    enabled: !!id,
  });

  const statusMutation = useMutation<unknown, Error, InvoiceStatus>({
    mutationFn: (status: InvoiceStatus) => api.put(`/api/invoices/${id}`, { status }),
    onSuccess: (_data, status) => {
      toast({ variant: 'success', message: `Invoice status updated to ${status}.` });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
      // Keep the client's Billing-tab invoice list in sync (bare prefix matches
      // ['client-invoices', clientId]).
      queryClient.invalidateQueries({ queryKey: ['client-invoices'] });
      setConfirmStatusStep(null);
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  // Adjust: append a signed invoice_adjustments row (POST /api/invoice-adjustments);
  // the band-65 rule handler rebalances the invoice balance server-side to
  // amount - SUM(adjustments) - SUM(cleared payments).
  const [adjustOpen, setAdjustOpen] = useState(false);
  // Capability gates (hooks — declared before the early returns). Mirror the endpoints:
  // Edit + status machine → PUT invoices (invoice.can_edit); Adjust / Installment plan →
  // billing add (invoice.can_add); Verify&send / Record-payment → the invoice workflow
  // (invoice.can_process). Previously these rendered on invoice STATUS alone — over-exposed
  // to view-only roles (training) + dead 403s for agent.
  const canEditInvoice = useEntityPermission('invoice', 'can_edit');
  const canAddInvoice = useEntityPermission('invoice', 'can_add');
  const canProcessInvoice = useEntityPermission('invoice', 'can_process');

  if (invoiceQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading invoice…</p>;
  }
  if (invoiceQuery.error) {
    const notFound = invoiceQuery.error instanceof ApiClientError && invoiceQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Invoice not found' : 'Failed to load invoice'}</h2>
        <p style={{ margin: 0 }}>{(invoiceQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/invoices')}>
            Back to Invoices
          </Button>
        </p>
      </div>
    );
  }
  if (!invoiceQuery.data) return null;

  const invoice = invoiceQuery.data;
  const items = itemsQuery.data ?? [];
  const payments = paymentsQuery.data ?? [];
  const installments = installmentsQuery.data ?? [];
  const notes = notesQuery.data ?? [];
  const next = nextStatusStep[invoice.status];
  const canRecordPayment = ['sent', 'partially_paid'].includes(invoice.status);
  const totalPaid = payments.reduce((sum, p) => sum + (p.amount ?? 0), 0);

  // ACH settlement lifecycle (05.4). A returned debit takes precedence over a
  // still-processing one; otherwise the most recent processing ref means the
  // invoice is held pending settlement (not yet cleared).
  const refs = ledgerQuery.data?.refs ?? [];
  const settlement: 'returned' | 'pending' | null =
    refs.some((r) => r.status === 'returned') ? 'returned'
      : refs.some((r) => r.status === 'processing') ? 'pending'
        : null;

  return (
    <>
      <EntityHeader
        prefix={`Invoice #${invoice.invoice_number}`}
        title={
          invoice.client_id
            ? <Link to={`/clients/${invoice.client_id}?tab=billing`} style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 4 }}>{clientQuery.data?.name ?? 'Loading client…'}</Link>
            : (clientQuery.data?.name ?? 'Loading client…')
        }
        subtitle={<>{`Tax year ${invoice.year} · ${invoice.type || 'standard'}`}<LastUpdated updatedBy={invoice.updated_by} updatedAt={invoice.updated_at} /></>}
        statusPills={<Badge variant={statusVariant[invoice.status]} dot>{invoice.status.replace(/_/g, ' ')}</Badge>}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/invoices')}>
              Back
            </Button>
            {canEditInvoice && (
              <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
            {next && canEditInvoice && (
              <Button variant="primary" size="sm" onClick={() => setConfirmStatusStep(next.status)}>
                {next.label}
              </Button>
            )}
            {['pending', 'prepared', 'approved', 'sent'].includes(invoice.status) && canProcessInvoice && (
              <Button variant="secondary" size="sm" onClick={() => setVerifyOpen(true)}>
                Verify &amp; send
              </Button>
            )}
            {['sent', 'partially_paid', 'paid'].includes(invoice.status) && canAddInvoice && (
              <Button variant="secondary" size="sm" onClick={() => setAdjustOpen(true)}>
                Adjust
              </Button>
            )}
            {['sent', 'partially_paid', 'approved'].includes(invoice.status) && installments.length === 0 && canAddInvoice && (
              <Button variant="secondary" size="sm" onClick={() => setPlanOpen(true)}>
                Installment plan
              </Button>
            )}
            {canRecordPayment && canProcessInvoice && (
              <Button variant="primary" size="sm" onClick={() => setPaymentOpen(true)}>
                Record payment
              </Button>
            )}
          </>
        }
        kpis={
          <>
            <StatCard
              label="Total"
              value={invoice.amount != null ? `$${invoice.amount.toLocaleString()}` : '—'}
              caption={`#${invoice.invoice_number}`}
              accent="hunter"
            />
            <StatCard
              label="Paid"
              value={totalPaid > 0 ? `$${totalPaid.toLocaleString()}` : '$0'}
              caption={`${payments.length} payment${payments.length === 1 ? '' : 's'}`}
              accent="green"
            />
            <StatCard
              label="Balance"
              value={invoice.balance != null ? `$${invoice.balance.toLocaleString()}` : '—'}
              caption={(invoice.balance ?? 0) > 0 ? 'Outstanding' : 'Settled'}
              accent={(invoice.balance ?? 0) > 0 ? 'amber' : 'green'}
            />
            <StatCard
              label="Due"
              value={formatDate(invoice.due_date)}
              caption={invoice.last_paid_date ? `Last paid ${formatDate(invoice.last_paid_date)}` : 'Not yet paid'}
              accent="info"
            />
          </>
        }
      />

      {settlement && (
        <div
          role="status"
          style={{
            marginTop: 'var(--sp-4)',
            display: 'flex', alignItems: 'center', gap: 'var(--sp-3)',
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--r-lg)',
            border: '1px solid ' + (settlement === 'returned' ? 'var(--color-status-red)' : 'var(--color-status-amber, var(--color-border))'),
            background: settlement === 'returned' ? 'var(--color-status-red-bg)' : 'var(--color-status-amber-bg, var(--color-card))',
            color: settlement === 'returned' ? 'var(--color-status-red)' : 'var(--color-ink)',
          }}
        >
          <Badge variant={settlement === 'returned' ? 'danger' : 'warning'} dot>
            {settlement === 'returned' ? 'ACH returned' : 'Pending settlement'}
          </Badge>
          <span style={{ fontSize: 'var(--fs-sm)' }}>
            {settlement === 'returned'
              ? 'A bank (ACH) payment was returned. The invoice has reverted to unpaid — follow up before re-collecting.'
              : 'A bank (ACH) payment is settling (typically ~4 business days). The invoice is held and will clear only when the funds settle.'}
          </span>
        </div>
      )}

      <Tabs
        activeValue={tab}
        onChange={(v) => setTab(v as typeof tab)}
        items={[
          { value: 'items', label: 'Line items', count: items.length },
          { value: 'payments', label: 'Payments', count: payments.length },
          { value: 'installments', label: 'Installments', count: installments.length },
          { value: 'notes', label: 'Notes', count: notes.length },
        ]}
      />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {tab === 'items' && (
          <ItemsTab
            items={items}
            loading={itemsQuery.isLoading}
            onRowClick={(it) => navigate(`/invoice-items/${it.id}`)}
            onParcelClick={(parcelId) => navigate(`/parcels/${parcelId}`)}
          />
        )}
        {tab === 'payments' && <PaymentsTab payments={payments} loading={paymentsQuery.isLoading} />}
        {tab === 'installments' && (
          <InstallmentsTab
            installments={installments}
            loading={installmentsQuery.isLoading}
            onGenerate={() => setPlanOpen(true)}
            onEdit={(inst) => setEditInstallment(inst)}
          />
        )}
        {tab === 'notes' && <EntityNotesPanel entityType="invoices" entityId={invoice.id} />}
      </div>

      <CreateEntityModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        table="invoices"
        recordId={invoice.id}
        invalidateKeys={[['invoices-list'], ['invoices', invoice.id]]}
      />

      <Modal
        open={!!confirmStatusStep}
        onClose={() => { if (!statusMutation.isPending) setConfirmStatusStep(null); }}
        title={confirmStatusStep ? `Confirm: move to ${confirmStatusStep}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmStatusStep(null)} disabled={statusMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={statusMutation.isPending}
              onClick={() => confirmStatusStep && statusMutation.mutate(confirmStatusStep)}
            >
              Confirm
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          This moves invoice <strong>#{invoice.invoice_number}</strong> from{' '}
          <strong>{invoice.status.replace(/_/g, ' ')}</strong> to{' '}
          <strong>{confirmStatusStep?.replace(/_/g, ' ')}</strong> and logs an audit entry.
        </p>
      </Modal>

      <RecordPaymentModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoice_number}
        outstandingBalance={invoice.balance}
      />

      <AdjustInvoiceModal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoice_number}
        items={items}
      />

      {verifyOpen && (
        <VerifyAndSendModal
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoice_number}
          onClose={() => setVerifyOpen(false)}
        />
      )}

      <GenerateInstallmentPlanModal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoice_number}
        outstandingBalance={invoice.balance}
      />

      {editInstallment && (
        <EditInstallmentModal
          installment={editInstallment}
          invoiceId={invoice.id}
          onClose={() => setEditInstallment(null)}
        />
      )}
    </>
  );
}

function ItemsTab({
  items, loading, onRowClick, onParcelClick,
}: {
  items: InvoiceItem[];
  loading: boolean;
  onRowClick: (it: InvoiceItem) => void;
  onParcelClick: (parcelId: string) => void;
}) {
  const columns = useMemo<ColumnDef<InvoiceItem>[]>(() => [
    {
      accessorKey: 'description',
      header: 'Description',
      // Y2 + Y3 (Yadira 2026-08-03) — the reason rides WITH the description rather than
      // in a column of its own, because it is present on a small minority of rows and an
      // almost-always-empty column reads as broken. The flag is what an operator must not
      // miss: a capped parcel bills a fee on a value reduction that saved the client no
      // tax, and this is the last surface before the invoice is sent.
      cell: ({ row, getValue }) => {
        const it = row.original as InvoiceItem & { no_tax_movement?: boolean | number; billing_reason?: string | null };
        const flagged = it.no_tax_movement === true || it.no_tax_movement === 1;
        return (
          <div>
            <div>{(getValue() as string) || '—'}</div>
            {flagged && (
              <span style={{
                display: 'inline-block', marginTop: 2, padding: '1px 6px',
                borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)',
                background: 'var(--color-status-amber-bg)', color: 'var(--color-status-amber)',
              }}>No tax movement</span>
            )}
            {it.billing_reason && (
              <div style={{ marginTop: 2, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                {it.billing_reason}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? `$${v.toLocaleString()}` : '—';
      },
    },
    {
      accessorKey: 'parcel_id',
      header: 'Parcel',
      cell: ({ getValue }) => {
        const id = getValue() as string | null;
        if (!id) return '—';
        return (
          <a
            onClick={(e) => { e.stopPropagation(); onParcelClick(id); }}
            style={{
              color: 'var(--brand-primary)',
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-xs)',
            }}
          >{id.slice(0, 8)}…</a>
        );
      },
    },
  ], [onParcelClick]);
  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading items…</p>;
  if (items.length === 0) return <Empty message="No line items on this invoice yet." />;
  return <DataTable data={items} columns={columns} onRowClick={onRowClick} />;
}

function PaymentsTab({ payments, loading }: { payments: InvoicePayment[]; loading: boolean }) {
  const columns = useMemo<ColumnDef<InvoicePayment>[]>(() => [
    { accessorKey: 'payment_date', header: 'Date', cell: ({ getValue }) => formatDate(getValue() as string | null) },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? `$${v.toLocaleString()}` : '—';
      },
    },
    { accessorKey: 'method', header: 'Method', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'check_number', header: 'Check #', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'reference_number', header: 'Reference', cell: ({ getValue }) => (getValue() as string) || '—' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const v = String(getValue() || 'pending');
        const variant = v === 'cleared' ? 'success' : v === 'refunded' ? 'warning' : v === 'failed' ? 'danger' : 'neutral';
        return <Badge variant={variant}>{v}</Badge>;
      },
    },
  ], []);
  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading payments…</p>;
  if (payments.length === 0) return <Empty message="No payments recorded on this invoice yet." />;
  return <DataTable data={payments} columns={columns} />;
}

const installmentStatusVariant: Record<InstallmentStatus, 'success' | 'warning' | 'neutral' | 'danger'> = {
  pending: 'neutral',
  paid: 'success',
  overdue: 'warning',
  cancelled: 'danger',
};

function InstallmentsTab({
  installments, loading, onGenerate, onEdit,
}: {
  installments: Installment[];
  loading: boolean;
  onGenerate: () => void;
  onEdit: (inst: Installment) => void;
}) {
  const columns = useMemo<ColumnDef<Installment>[]>(() => [
    { accessorKey: 'sequence', header: '#', cell: ({ getValue }) => (getValue() as number) ?? '—' },
    { accessorKey: 'due_date', header: 'Due date', cell: ({ getValue }) => formatDate(getValue() as string | null) },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? `$${v.toLocaleString()}` : '—';
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const v = String(getValue() || 'pending') as InstallmentStatus;
        return <Badge variant={installmentStatusVariant[v] ?? 'neutral'}>{v}</Badge>;
      },
    },
    { accessorKey: 'paid_date', header: 'Paid date', cell: ({ getValue }) => formatDate(getValue() as string | null) },
  ], []);

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading installment schedule…</p>;
  if (installments.length === 0) {
    return (
      <div style={{
        padding: 'var(--sp-6)',
        background: 'var(--color-card)',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--r-lg)',
        color: 'var(--color-muted)',
        textAlign: 'center',
        fontSize: 'var(--fs-md)',
      }}>
        <p style={{ margin: '0 0 var(--sp-3)' }}>No installment schedule on this invoice — the balance is due in a single payment.</p>
        <Button variant="primary" size="sm" onClick={onGenerate}>Generate installment plan</Button>
      </div>
    );
  }

  const total = installments.reduce((s, i) => s + (i.amount ?? 0), 0);
  const anyMoney = installments.some((i) => i.amount != null);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          {installments.length} installment{installments.length === 1 ? '' : 's'}
          {anyMoney ? ` · scheduled total $${total.toLocaleString()}` : ''}
        </span>
        <Button variant="secondary" size="sm" onClick={onGenerate}>Regenerate plan</Button>
      </div>
      <DataTable data={installments} columns={columns} onRowClick={onEdit} />
      <p style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
        Click a row to edit its due date, amount, or status.
      </p>
    </div>
  );
}

/**
 * GenerateInstallmentPlanModal — POST /api/billing/installments/:invoiceId
 * { count, first_due_date, interval_days }. The backend splits the invoice
 * balance into `count` dated slices (penny remainder on the last), so no
 * client-side money math happens here. Regenerating replaces the prior rows.
 */
function GenerateInstallmentPlanModal({
  open, onClose, invoiceId, invoiceNumber, outstandingBalance,
}: {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber: string;
  outstandingBalance: number | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [count, setCount] = useState('4');
  const [firstDueDate, setFirstDueDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [intervalDays, setIntervalDays] = useState('30');

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/billing/installments/${invoiceId}`, {
      count: Number(count),
      first_due_date: firstDueDate,
      interval_days: Number(intervalDays),
    }),
    onSuccess: () => {
      toast({ variant: 'success', message: `Installment plan of ${Number(count)} generated.` });
      queryClient.invalidateQueries({ queryKey: ['invoice-installments', invoiceId] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const n = Number(count);
  const canSubmit = count.trim() !== '' && Number.isFinite(n) && n >= 1 && firstDueDate.trim() !== ''
    && Number(intervalDays) >= 1;

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title={`Installment plan: Invoice #${invoiceNumber}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Generate plan
          </Button>
        </>
      }
    >
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Number of installments</span>
        <Input type="number" min={1} value={count} onChange={e => setCount(e.target.value)} required />
        {outstandingBalance != null && (
          <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Splits the ${outstandingBalance.toLocaleString()} balance into {Number.isFinite(n) && n >= 1 ? n : '—'} even slices.
          </div>
        )}
      </label>
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>First due date</span>
        <Input type="date" value={firstDueDate} onChange={e => setFirstDueDate(e.target.value)} required />
      </label>
      <label style={{ display: 'block' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Days between installments</span>
        <Input type="number" min={1} value={intervalDays} onChange={e => setIntervalDays(e.target.value)} required />
      </label>
    </Modal>
  );
}

/**
 * EditInstallmentModal — edit a single scheduled slice via the generic CRUD
 * resource PUT /api/invoice-installments/:id { due_date, amount, status, paid_date }.
 * Delete removes the slice (DELETE /api/invoice-installments/:id).
 */
function EditInstallmentModal({
  installment, invoiceId, onClose,
}: {
  installment: Installment;
  invoiceId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [dueDate, setDueDate] = useState(installment.due_date ?? '');
  const [amount, setAmount] = useState(installment.amount != null ? String(installment.amount) : '');
  const [status, setStatus] = useState<InstallmentStatus>(installment.status);
  const [paidDate, setPaidDate] = useState(installment.paid_date ?? '');

  const onError = (err: Error) => {
    const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
      ? String((err.body as { error: unknown }).error)
      : err.message;
    toast({ variant: 'error', message: msg });
  };
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['invoice-installments', invoiceId] });

  const save = useMutation<unknown, Error, void>({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        due_date: dueDate,
        status,
        paid_date: status === 'paid' ? (paidDate || new Date().toISOString().split('T')[0]) : (paidDate || null),
      };
      // amount is money-blind-redacted for administrators; only send it back when
      // the user actually has (and edited) a real number, never a redacted null.
      if (amount.trim() !== '' && !isNaN(Number(amount))) body.amount = Number(amount);
      return api.put(`/api/invoice-installments/${installment.id}`, body);
    },
    onSuccess: () => {
      toast({ variant: 'success', message: `Installment #${installment.sequence} updated.` });
      invalidate();
      onClose();
    },
    onError,
  });

  const remove = useMutation<unknown, Error, void>({
    mutationFn: () => api.delete(`/api/invoice-installments/${installment.id}`),
    onSuccess: () => {
      toast({ variant: 'success', message: `Installment #${installment.sequence} removed.` });
      invalidate();
      onClose();
    },
    onError,
  });

  const busy = save.isPending || remove.isPending;
  const canSubmit = dueDate.trim() !== '';

  return (
    <Modal
      open
      onClose={() => { if (!busy) onClose(); }}
      title={`Edit installment #${installment.sequence}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => remove.mutate()} loading={remove.isPending} disabled={busy}>
            Remove
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!canSubmit || busy}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Due date</span>
        <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} required />
      </label>
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Amount ($)</span>
        <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder={installment.amount == null ? 'Redacted' : undefined} />
        {installment.amount == null && (
          <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Amount is hidden for your role; leave blank to keep it unchanged.
          </div>
        )}
      </label>
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Status</span>
        <Select
          value={status}
          onChange={e => setStatus(e.target.value as InstallmentStatus)}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'paid', label: 'Paid' },
            { value: 'overdue', label: 'Overdue' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
      </label>
      {status === 'paid' && (
        <label style={{ display: 'block' }}>
          <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Paid date</span>
          <Input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
        </label>
      )}
    </Modal>
  );
}

function RecordPaymentModal({
  open, onClose, invoiceId, invoiceNumber, outstandingBalance,
}: {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber: string;
  outstandingBalance: number | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [amount, setAmount] = useState(outstandingBalance ? String(outstandingBalance) : '');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [method, setMethod] = useState('manual');
  const [checkNumber, setCheckNumber] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');

  const mutation = useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`/api/workflow/mark-invoice-paid/${invoiceId}`, {
      amount: Number(amount),
      payment_date: paymentDate,
      method: method || null,
      // check_number is a first-class field (persists once the backend adds it
      // to the recordInvoicePayment INSERT — column already exists).
      check_number: method === 'check' ? (checkNumber || null) : null,
      reference_number: referenceNumber || null,
      notes: notes || null,
    }),
    onSuccess: () => {
      toast({ variant: 'success', message: `Payment of $${Number(amount).toLocaleString()} recorded.` });
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoiceId] });
      // Closing the invoice settles its pending installments server-side, so
      // refresh the Installments tab too (was showing 'pending' after payment).
      queryClient.invalidateQueries({ queryKey: ['invoice-installments', invoiceId] });
      // Refresh the client's Billing-tab invoice list (bare prefix matches
      // ['client-invoices', clientId]) so its balance/status columns aren't stale.
      queryClient.invalidateQueries({ queryKey: ['client-invoices'] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const canSubmit = amount.trim() !== '' && !isNaN(Number(amount)) && paymentDate.trim() !== '';

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title={`Record payment: Invoice #${invoiceNumber}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Record payment
          </Button>
        </>
      }
    >
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Amount ($)</span>
        <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} required />
        {outstandingBalance != null && (
          <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Outstanding balance: ${outstandingBalance.toLocaleString()}
          </div>
        )}
      </label>
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Payment date</span>
        <Input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} required />
      </label>
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Method</span>
        <Select
          value={method}
          onChange={e => setMethod(e.target.value)}
          options={[
            { value: 'manual', label: 'Manual entry' },
            { value: 'check', label: 'Check' },
            { value: 'ach', label: 'ACH' },
            { value: 'wire', label: 'Wire' },
            { value: 'stripe', label: 'Stripe' },
            { value: 'refund', label: 'Refund' },
            { value: 'other', label: 'Other' },
          ]}
        />
      </label>
      {method === 'check' && (
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Check number</span>
          <Input value={checkNumber} onChange={e => setCheckNumber(e.target.value)} placeholder="e.g. 1042" />
        </label>
      )}
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Reference # (optional)</span>
        <Input value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} placeholder="ACH trace / wire confirmation" />
      </label>
      <label style={{ display: 'block' }}>
        <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>Notes (optional)</span>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={2}
          style={{
            width: '100%',
            padding: 'var(--sp-2) var(--sp-3)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            background: 'var(--color-card)',
            color: 'var(--color-ink)',
            fontSize: 'var(--fs-md)',
            fontFamily: 'inherit',
            resize: 'vertical',
            outline: 'none',
          }}
        />
      </label>
    </Modal>
  );
}

interface AdjustResult {
  invoice_id: string;
  adjustment_id: string;
  kind: string;
  amount?: number;
  balance?: number;
  prorated_total?: number;
}

/**
 * AdjustInvoiceModal — POST /api/billing/adjust-invoice/:invoiceId (backlog 04.7).
 *
 * Proration mode auto-prorates every line item by a percentage, with an optional
 * per-account (single line item) override, and the backend emits one signed
 * proration adjustment that the band-65 rule rebalances into invoices.balance.
 * The other kinds (credit/discount/surcharge/write-off) pass a manual signed
 * amount through the same endpoint. No client-side balance math happens here —
 * the server returns the rebalanced balance.
 */
function AdjustInvoiceModal({
  open, onClose, invoiceId, invoiceNumber, items,
}: {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber: string;
  items: InvoiceItem[];
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [kind, setKind] = useState('proration');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  // Proration: a whole-invoice percentage (0–100) plus per-item overrides keyed
  // by invoice_item id. An empty override string means "use the global percent".
  const [prorationPct, setProrationPct] = useState('50');
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const isProration = kind === 'proration';

  const mutation = useMutation<AdjustResult, Error, void>({
    mutationFn: () => {
      if (isProration) {
        const factor = Math.min(1, Math.max(0, Number(prorationPct) / 100));
        const overridePayload = Object.entries(overrides)
          .filter(([, v]) => v.trim() !== '' && !isNaN(Number(v)))
          .map(([item_id, v]) => ({ item_id, factor: Math.min(1, Math.max(0, Number(v) / 100)) }));
        return api.post<AdjustResult>(`/api/billing/adjust-invoice/${invoiceId}`, {
          kind: 'proration',
          proration_factor: factor,
          overrides: overridePayload,
          description: description || null,
        });
      }
      return api.post<AdjustResult>(`/api/billing/adjust-invoice/${invoiceId}`, {
        kind,
        amount: Number(amount),
        description: description || null,
      });
    },
    onSuccess: (res) => {
      const msg = res.balance != null
        ? `Invoice ${res.kind} applied — balance now $${Number(res.balance).toLocaleString()}.`
        : `Invoice ${res.kind} applied.`;
      toast({ variant: 'success', message: msg });
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-items', invoiceId] });
      // The Billing-tab invoice list reads balance/status too — keep it fresh.
      queryClient.invalidateQueries({ queryKey: ['client-invoices'] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const canSubmit = isProration
    ? prorationPct.trim() !== '' && !isNaN(Number(prorationPct))
    : amount.trim() !== '' && !isNaN(Number(amount)) && Number(amount) !== 0;

  const labelStyle: React.CSSProperties = {
    display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)',
    fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)',
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) onClose(); }}
      title={`Adjust invoice #${invoiceNumber}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Apply adjustment
          </Button>
        </>
      }
    >
      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={labelStyle}>Kind</span>
        <Select
          value={kind}
          onChange={e => setKind(e.target.value)}
          options={[
            { value: 'proration', label: 'Proration (auto)' },
            { value: 'credit', label: 'Credit' },
            { value: 'discount', label: 'Discount' },
            { value: 'surcharge', label: 'Surcharge' },
            { value: 'writeoff', label: 'Write-off' },
          ]}
        />
      </label>

      {isProration ? (
        <>
          <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
            <span style={labelStyle}>Bill percentage (applies to every line)</span>
            <Input type="number" min={0} max={100} value={prorationPct} onChange={e => setProrationPct(e.target.value)} required />
            <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
              e.g. 50 bills half of each line item; the balance rebalances to the prorated total.
            </div>
          </label>
          {items.length > 0 && (
            <div style={{ marginBottom: 'var(--sp-3)' }}>
              <span style={labelStyle}>Per-account override (optional)</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                {items.map((it) => (
                  <div key={it.id} style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
                    <span style={{ flex: 1, fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.description || (it.parcel_id ? `${it.parcel_id.slice(0, 8)}…` : it.id.slice(0, 8))}
                    </span>
                    <div style={{ width: 96 }}>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={overrides[it.id] ?? ''}
                        placeholder={prorationPct}
                        onChange={e => setOverrides(prev => ({ ...prev, [it.id]: e.target.value }))}
                      />
                    </div>
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>%</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                Leave blank to use the invoice percentage above.
              </div>
            </div>
          )}
        </>
      ) : (
        <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
          <span style={labelStyle}>Amount ($, signed)</span>
          <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} required />
          <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            Negative reduces the balance owed (credit/discount); positive increases it (surcharge).
          </div>
        </label>
      )}

      <label style={{ display: 'block' }}>
        <span style={labelStyle}>Description (optional)</span>
        <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. mid-year proration" />
      </label>
    </Modal>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}

interface VerifyCheck {
  key: string;
  label: string;
  severity: 'block' | 'warn';
  passed: boolean;
  detail: string;
}

interface Recipient {
  contact_id: string;
  name: string | null;
  email: string;
  role: string | null;
  is_primary: boolean;
}

interface PreSendChecks {
  invoice_id: string;
  status: InvoiceStatus;
  checks: VerifyCheck[];
  blocked: boolean;
  recipients: Recipient[];
}

interface SendResult {
  invoice: Invoice;
  recipients: Recipient[];
  email: { id: string; to: string[]; status: string };
}

/**
 * VerifyAndSendModal — the REAL pre-invoice verification + send flow (items
 * 04.9/04.10/08.5). Fetches the live blocking-checks registry from
 * GET /api/invoices/:id/pre-send-checks (rendering the real block/warn results +
 * the resolved billing recipients) and, when no blocking check fails, POSTs to
 * /api/invoices/:id/send — which transitions the invoice approved→sent, renders +
 * auto-files the PDF, and emails the recipients via the outbox keystone.
 */
function VerifyAndSendModal({ invoiceId, invoiceNumber, onClose }: { invoiceId: string; invoiceNumber: string; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const checksQuery = useQuery<PreSendChecks>({
    queryKey: ['invoice-pre-send', invoiceId],
    queryFn: ({ signal }) => api.get<PreSendChecks>(`/api/invoices/${invoiceId}/pre-send-checks`, signal),
  });

  const send = useMutation<SendResult, Error>({
    mutationFn: () => api.post<SendResult>(`/api/invoices/${invoiceId}/send`, {}),
    onSuccess: (data) => {
      toast({ variant: 'success', message: `Invoice emailed to ${data.recipients.length} recipient${data.recipients.length === 1 ? '' : 's'}.` });
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
      // Keep the client's Billing-tab invoice list in sync (matches the
      // status-machine + record-payment handlers; bare prefix hits ['client-invoices', clientId]).
      queryClient.invalidateQueries({ queryKey: ['client-invoices'] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
      // A 409 refusal carries the fresh failing set — re-read so the UI reflects it.
      queryClient.invalidateQueries({ queryKey: ['invoice-pre-send', invoiceId] });
    },
  });

  const data = checksQuery.data;
  const hasBlocks = data ? data.blocked : true;
  const recipients = data?.recipients ?? [];

  return (
    <Modal open onClose={onClose} title="Verify &amp; send" size="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        {checksQuery.isLoading && <p style={{ margin: 0, color: 'var(--color-ink-3)' }}>Running pre-send checks…</p>}
        {checksQuery.error && (
          <p style={{ margin: 0, color: 'var(--color-status-red)' }}>
            {(checksQuery.error as Error).message}
          </p>
        )}
        {data && data.checks.map((c) => (
          <div key={c.key} style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start' }}>
            <Badge variant={c.passed ? 'success' : c.severity === 'block' ? 'danger' : 'warning'} dot>
              {c.passed ? 'Pass' : c.severity === 'block' ? 'Block' : 'Warn'}
            </Badge>
            <div>
              <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{c.label}</div>
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>{c.detail}</div>
            </div>
          </div>
        ))}

        {data && (
          <div>
            <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)', marginBottom: 'var(--sp-1)' }}>
              Recipients ({recipients.length})
            </div>
            {recipients.length === 0 ? (
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                No client contact is flagged to receive invoices.
              </div>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 'var(--sp-4)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
                {recipients.map((r) => (
                  <li key={r.contact_id}>
                    {r.name || r.email} &lt;{r.email}&gt;
                    {r.is_primary && <Badge variant="info" style={{ marginLeft: 'var(--sp-2)' }}>Primary</Badge>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
          Sending emails invoice #{invoiceNumber} to the recipients above, files the PDF to the client's documents, and marks the invoice sent. Send is disabled while any blocking check fails.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={send.isPending}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            loading={send.isPending}
            disabled={hasBlocks || checksQuery.isLoading || send.isPending}
            onClick={() => send.mutate()}
          >
            Email invoice
          </Button>
        </div>
      </div>
    </Modal>
  );
}
